import ToggleInput from "components/Properties/InputComponents/ToggleInput";
import ColorBoxInput from "components/Properties/InputComponents/ColorboxInput";
import DropDownInput from "components/Properties/InputComponents/DropDownInput";
import {NumberSliderInput} from "components/Properties/InputComponents/NumberInput";
import BaseComponent from "../BaseComponent";

const postProcessingBlendModes = [
  "ADD",
  "ALPHA",
  "AVERAGE",
  "COLOR",
  "COLOR_BURN",
  "COLOR_DODGE",
  "DARKEN",
  "DIFFERENCE",
  "DIVIDE",
  "DST",
  "EXCLUSION",
  "HARD_LIGHT",
  "HARD_MIX",
  "HUE",
  "INVERT",
  "INVERT_RGB",
  "LIGHTEN",
  "LINEAR_BURN",
  "LINEAR_DODGE",
  "LINEAR_LIGHT",
  "LUMINOSITY",
  "MULTIPLY",
  "NEGATION",
  "NORMAL",
  "OVERLAY",
  "PIN_LIGHT",
  "REFLECT",
  "SATURATION",
  "SCREEN",
  "SOFT_LIGHT",
  "SRC",
  "SUBTRACT",
  "VIVID_LIGHT",
];

const PostProcessingComponent = ({
  scene,
  checkboxOnChange,
  colorOnChange,
  dropdownOnChange,
  dropdownOpened,
  setDropdownOpened,
  inputOnChange,
}) => {
  const postProcessing = scene.userData.postProcessing;
  const ssao = postProcessing.ssao ? Object.keys(postProcessing.ssao).length !== 0 : false;
  const bloom = postProcessing.bloom ? Object.keys(postProcessing.bloom).length !== 0 : false;
  const godrays = postProcessing.godrays ? Object.keys(postProcessing.godrays).length !== 0 : false;
  const hueSaturation = postProcessing.hueSaturation ? Object.keys(postProcessing.hueSaturation).length !== 0 : false;
  const brightnessContrast = postProcessing.brightnessContrast ? Object.keys(postProcessing.brightnessContrast).length !== 0 : false;

  return (
    <BaseComponent title="Post Processing" foldable={true}>
      <ToggleInput name="SSAO" checked={ssao} checkboxOnChange={checkboxOnChange} />
      {ssao && (
        <>
          <ColorBoxInput name="SSAOColor" objectColor={postProcessing.ssao.color} colorOnChange={colorOnChange} depth={1} />
          <NumberSliderInput
            name="Bias"
            value={postProcessing.ssao.bias}
            inputOnChange={inputOnChange}
            min={0}
            max={0.1}
            step={0.01}
            depth={1}
          />
          <NumberSliderInput
            name="SSAOIntensity"
            value={postProcessing.ssao.intensity}
            inputOnChange={inputOnChange}
            min={0}
            max={60}
            step={0.1}
            depth={1}
          />
          <NumberSliderInput
            name="Fade"
            value={postProcessing.ssao.fade}
            inputOnChange={inputOnChange}
            min={0}
            max={0.1}
            step={0.01}
            depth={1}
          />
        </>
      )}
      <ToggleInput name="Bloom" checked={bloom} checkboxOnChange={checkboxOnChange} />
      {bloom && (
        <>
          <DropDownInput
            name="BloomBlendMode"
            array={postProcessingBlendModes}
            dropdownOnChange={dropdownOnChange}
            value={postProcessing.bloom.blendFunction}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            depth={1}
          />
          <NumberSliderInput
            name="BloomIntensity"
            value={postProcessing.bloom.intensity}
            inputOnChange={inputOnChange}
            min={0}
            max={10}
            step={0.1}
            depth={1}
          />
          <NumberSliderInput
            name="BloomRadius"
            value={postProcessing.bloom.radius}
            inputOnChange={inputOnChange}
            min={0}
            max={1}
            step={0.001}
            depth={1}
          />
          <NumberSliderInput
            name="BloomOpacity"
            value={postProcessing.bloom.opacity}
            inputOnChange={inputOnChange}
            min={0}
            max={1}
            step={0.01}
            depth={1}
          />
          <ToggleInput
            name="Luminance"
            checked={scene.userData.postProcessing.bloom.luminance}
            checkboxOnChange={checkboxOnChange}
            depth={1}
            hasChildren={true}
          />
          {scene.userData.postProcessing.bloom.luminance && (
            <>
              <ToggleInput
                name="Inverted"
                checked={scene.userData.postProcessing.bloom.inverted}
                checkboxOnChange={checkboxOnChange}
                depth={2}
              />
              <NumberSliderInput
                name="LuminanceThreshold"
                value={postProcessing.bloom.threshold}
                inputOnChange={inputOnChange}
                min={0}
                max={1}
                step={0.001}
                depth={2}
              />
              <NumberSliderInput
                name="LuminanceSmoothing"
                value={postProcessing.bloom.smoothing}
                inputOnChange={inputOnChange}
                min={0}
                max={1}
                step={0.001}
                depth={2}
              />
            </>
          )}
        </>
      )}
      <ToggleInput name="God-rays" checked={godrays} checkboxOnChange={checkboxOnChange} />
      {godrays && (
        <>
          <ColorBoxInput name="GodraysColor" objectColor={postProcessing.godrays.color} colorOnChange={colorOnChange} depth={1} />
          <NumberSliderInput
            name="GodraysWeight"
            value={postProcessing.godrays.weight}
            inputOnChange={inputOnChange}
            min={0}
            max={1}
            step={0.01}
            depth={1}
          />
          <NumberSliderInput
            name="GodraysDensity"
            value={postProcessing.godrays.density}
            inputOnChange={inputOnChange}
            min={0}
            max={1}
            step={0.01}
            depth={1}
          />
        </>
      )}
      <ToggleInput name="HueSaturation" checked={hueSaturation} checkboxOnChange={checkboxOnChange} />
      {hueSaturation && (
        <>
          <DropDownInput
            name="HSVBlendMode"
            array={postProcessingBlendModes}
            dropdownOnChange={dropdownOnChange}
            value={postProcessing.hueSaturation.blendFunction}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            depth={1}
          />
          <NumberSliderInput
            name="Hue"
            value={postProcessing.hueSaturation.hue}
            inputOnChange={inputOnChange}
            min={-180}
            max={180}
            step={1}
            depth={1}
          />
          <NumberSliderInput
            name="Saturation"
            value={postProcessing.hueSaturation.saturation}
            inputOnChange={inputOnChange}
            min={-1}
            max={1}
            step={0.01}
            depth={1}
          />
          <NumberSliderInput
            name="HSVOpacity"
            value={postProcessing.hueSaturation.opacity}
            inputOnChange={inputOnChange}
            min={0}
            max={1}
            step={0.01}
            depth={1}
          />
        </>
      )}
      <ToggleInput name="BrightnessContrast" checked={brightnessContrast} checkboxOnChange={checkboxOnChange} />
      {brightnessContrast && (
        <>
          <DropDownInput
            name="BCBlendMode"
            array={[
              "ADD",
              "ALPHA",
              "AVERAGE",
              "COLOR",
              "COLOR_BURN",
              "COLOR_DODGE",
              "DARKEN",
              "DIFFERENCE",
              "DIVIDE",
              "DST",
              "EXCLUSION",
              "HARD_LIGHT",
              "HARD_MIX",
              "HUE",
              "INVERT",
              "INVERT_RGB",
              "LIGHTEN",
              "LINEAR_BURN",
              "LINEAR_DODGE",
              "LINEAR_LIGHT",
              "LUMINOSITY",
              "MULTIPLY",
              "NEGATION",
              "NORMAL",
              "OVERLAY",
              "PIN_LIGHT",
              "REFLECT",
              "SATURATION",
              "SCREEN",
              "SOFT_LIGHT",
              "SRC",
              "SUBTRACT",
              "VIVID_LIGHT",
            ]}
            dropdownOnChange={dropdownOnChange}
            value={postProcessing.brightnessContrast.blendFunction}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            depth={1}
          />
          <NumberSliderInput
            name="BCBrightness"
            value={postProcessing.brightnessContrast.brightness}
            inputOnChange={inputOnChange}
            min={-1}
            max={1}
            step={0.001}
            depth={1}
          />
          <NumberSliderInput
            name="BCContrast"
            value={postProcessing.brightnessContrast.contrast}
            inputOnChange={inputOnChange}
            min={-1}
            max={1}
            step={0.001}
            depth={1}
          />
          <NumberSliderInput
            name="BCOpacity"
            value={postProcessing.brightnessContrast.opacity}
            inputOnChange={inputOnChange}
            min={0}
            max={1}
            step={0.01}
            depth={1}
          />
        </>
      )}
    </BaseComponent>
  );
};

export default PostProcessingComponent;
